import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  Building2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Clock,
  FileSpreadsheet,
  Inbox,
  Layers,
  MapPin,
  Package,
  Printer,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Minus,
  Users,
  BarChart3,
} from 'lucide-react';
import { useSiteContext } from '../SiteContext';
import config from '../configLoader';
import FloatMenuButton, { FloatMenuOption } from '../components/v2/FloatMenuButton';
import { useSelectPayPeriod } from '../hooks/useSelectPayPeriod';
import { PayPeriod } from '../interfaces/PayPeriodInterface';
import {
  getProjectWorkList,
  getProjectWorkDetail,
  getProjectWorkSupervisors,
  downloadProjectWorkExport,
} from '../api/projectWork';
import {
  ProjectWorkList,
  ProjectWorkListRow,
  ProjectWorkDetail,
  ProjectWorkPunch,
  ProjectWorkPunchType,
  ProjectWeekPoint,
  ProjectWorkTrend,
  ProjectWorkSupervisorOption,
  ProjectSupervisorInfo,
  SupervisorProjectRole,
} from '../interfaces/ProjectWork';
import FilterBar, { FilterEmpty } from '../components/FilterBar';
import Highlight from '../components/Highlight';
import useDebouncedValue from '../hooks/useDebouncedValue';
import {
  filterCodeRollup,
  filterEmployees,
  filterProjectList,
  normalizeTerm,
  punchScopeNote,
  EmployeeScope,
  FilteredEmployee,
} from '../util/projectWorkFilter';
import './ProjectWorkPage.css';

// ─── Helpers ───────────────────────────────────────────────────────────────

const hrs = (minutes: number): string => (minutes / 60).toFixed(1);

const money = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Abbreviated money for KPI tiles: $5.3k, $1.2M. */
const moneyShort = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
};

/** Convert a minutes-based trend to an hours-based one for display. */
const toHourTrend = (t: ProjectWorkTrend): ProjectWorkTrend => ({
  current: t.current / 60,
  previous: t.previous / 60,
  delta: t.delta / 60,
});

const TIER_LABEL_FALLBACKS: Record<SupervisorProjectRole, string> = {
  secondary: 'Secondary',
  tertiary: 'Finish',
  fourth: 'Fourth',
  fifth: 'Fifth',
};

const tierLabel = (role: SupervisorProjectRole): string =>
  config.supervisorTierLabels?.[role] ?? TIER_LABEL_FALLBACKS[role];

const fmtShort = (iso: string): string => {
  const d = new Date(iso + 'T12:00:00Z');
  const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${mons[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** Minutes since local midnight -> "7:04a" / "12:07p"; "(open)" for a punch with no out yet. */
const fmtPunchTime = (minutes: number | null): string => {
  if (minutes == null) return '(open)';
  const h24 = Math.floor(minutes / 60) % 24;
  const mm = String(minutes % 60).padStart(2, '0');
  return `${h24 % 12 || 12}:${mm}${h24 >= 12 ? 'p' : 'a'}`;
};

/** "Mon" + "6/22" for the punch table's date cell. */
const fmtPunchDate = (iso: string): { dow: string; md: string } => {
  const d = new Date(iso + 'T12:00:00Z');
  const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return { dow: dows[d.getUTCDay()], md: `${d.getUTCMonth() + 1}/${d.getUTCDate()}` };
};

const fmtDayLabel = (iso: string): { dow: string; md: string } => {
  const d = new Date(iso + 'T12:00:00Z');
  const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return { dow: dows[d.getUTCDay()], md: `${mons[d.getUTCMonth()]} ${d.getUTCDate()}` };
};

// ─── Small presentational pieces ─────────────────────────────────────────────

/** Compose a single-line address from the project's parts. */
const fullAddress = (d: { address: string | null; city: string | null; state: string | null; zip: string | null }): string => {
  const cityStateZip = [d.city, [d.state, d.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [d.address, cityStateZip].filter(Boolean).join(', ');
};

/** Each supervisor shown with its own tier tag (not comma-separated). */
const SupervisorTags: React.FC<{ supervisors: ProjectSupervisorInfo[] }> = ({ supervisors }) => (
  <span className="pw-sup-tags">
    {supervisors.map((s) => (
      <span key={`${s.role}-${s.employeeID}`} className="pw-sup">
        <span className={`pw-tier pw-tier-${s.role}`}>{tierLabel(s.role)}</span>
        {s.name}
      </span>
    ))}
  </span>
);

/** Overlay spinner that sits on top of stale content (no layout shift on reload). */
const LoadingOverlay: React.FC = () => (
  <div className="pw-overlay">
    <div className="pw-spinner" />
    <span>Loading…</span>
  </div>
);

/** Centered spinner for first loads (when there is no prior content to keep). */
const CenteredLoader: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <div className="pw-loading">
    <div className="pw-spinner" />
    <span>{label}</span>
  </div>
);

/** Inline bar sparkline of hourly minutes; last bar highlighted. */
const Sparkline: React.FC<{ points: ProjectWeekPoint[] }> = ({ points }) => {
  const vals = points.map((p) => p.totalHourlyMinutes);
  const max = Math.max(1, ...vals);
  const W = 132;
  const H = 34;
  const gap = 3;
  const n = Math.max(vals.length, 1);
  const bw = (W - gap * (n - 1)) / n;
  return (
    <svg className="pw-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden>
      {vals.map((v, i) => {
        const h = Math.max(1, (v / max) * (H - 2));
        const x = i * (bw + gap);
        const last = i === vals.length - 1;
        return (
          <rect
            key={i}
            x={x}
            y={H - h}
            width={bw}
            height={h}
            rx={1.5}
            className={last ? 'pw-spark-bar pw-spark-last' : 'pw-spark-bar'}
          />
        );
      })}
    </svg>
  );
};

const DeltaBadge: React.FC<{ trend: ProjectWorkTrend; suffix?: string; points?: boolean }> = ({
  trend,
  suffix = '',
  points = false,
}) => {
  const d = trend.delta;
  const dir = d > 0 ? 'up' : d < 0 ? 'down' : 'flat';
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  const val = Math.abs(d);
  const shown = points ? val.toFixed(1) : suffix === '$' ? money(val).replace('$', '') : val.toFixed(1);
  return (
    <span className={`pw-delta pw-delta-${dir}`}>
      <Icon size={12} />
      {sign}
      {suffix === '$' ? '$' : ''}
      {shown}
      {suffix && suffix !== '$' ? suffix : ''}
    </span>
  );
};

// ─── Activity chart (detail) ─────────────────────────────────────────────────

interface ChartProps {
  series: ProjectWeekPoint[];
  metric: 'hours' | 'dollars';
  selectedPayPeriodID: number;
  onPick: (payPeriodID: number) => void;
}

const ActivityChart: React.FC<ChartProps> = ({ series, metric, selectedPayPeriodID, onPick }) => {
  const [hover, setHover] = useState<number | null>(null);
  const W = 760;
  const H = 214;
  const padL = 34;
  const padB = 30;
  const padT = 48; // headroom so the hover tooltip sits above the bars, not over them
  const plotW = W - padL - 8;
  const plotH = H - padB - padT;
  const baseY = padT + plotH;

  const barVal = (p: ProjectWeekPoint) =>
    metric === 'hours'
      ? (p.totalHourlyMinutes + p.totalPieceClockMinutes) / 60
      : p.totalPieceDollars;
  const maxBar = Math.max(1, ...series.map(barVal));

  const n = Math.max(series.length, 1);
  const slot = plotW / n;
  const bw = Math.min(26, slot * 0.62);

  // % complete line points (0–100 axis)
  const linePts = series
    .map((p, i) => {
      const x = padL + i * slot + slot / 2;
      const y = padT + plotH - (Math.min(100, p.pctComplete) / 100) * plotH;
      return `${x},${y}`;
    })
    .join(' ');
  // Soft area fill under the % complete line (line points + down to the baseline at each end).
  const firstX = padL + slot / 2;
  const lastX = padL + (n - 1) * slot + slot / 2;
  const areaPts = `${firstX},${baseY} ${linePts} ${lastX},${baseY}`;

  const gridVals = [0, 0.25, 0.5, 0.75, 1];

  // Tooltip geometry for the hovered week (clamped to the plot edges).
  const tip = (() => {
    if (hover == null || !series[hover]) return null;
    const p = series[hover];
    const cx = padL + hover * slot + slot / 2;
    const boxW = 176;
    const boxH = 42;
    const bx = Math.max(padL, Math.min(cx - boxW / 2, W - 8 - boxW));
    return { p, bx, by: 2, boxW, boxH };
  })();

  return (
    <svg
      className="pw-chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      onMouseLeave={() => setHover(null)}
    >
      {gridVals.map((g, i) => {
        const y = padT + plotH - g * plotH;
        const label = metric === 'hours' ? `${Math.round(g * maxBar)}h` : moneyShort(g * maxBar);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - 8} y2={y} className="pw-chart-grid" />
            <text x={padL - 6} y={y + 3} className="pw-chart-axis" textAnchor="end">
              {label}
            </text>
          </g>
        );
      })}
      {series.map((p, i) => {
        const x = padL + i * slot + (slot - bw) / 2;
        const isSel = p.payPeriodID === selectedPayPeriodID;
        if (metric === 'hours') {
          const hH = (p.totalHourlyMinutes / 60 / maxBar) * plotH;
          const pcH = (p.totalPieceClockMinutes / 60 / maxBar) * plotH;
          return (
            <g key={i}>
              {isSel && <rect x={x - 2} y={padT} width={bw + 4} height={plotH} className="pw-bar-sel" />}
              <rect x={x} y={baseY - hH} width={bw} height={hH} className="pw-bar-hourly" />
              <rect x={x} y={baseY - hH - pcH} width={bw} height={pcH} className="pw-bar-piececlock" />
            </g>
          );
        }
        const bh = (p.totalPieceDollars / maxBar) * plotH;
        return (
          <g key={i}>
            {isSel && <rect x={x - 2} y={padT} width={bw + 4} height={plotH} className="pw-bar-sel" />}
            <rect x={x} y={baseY - bh} width={bw} height={bh} className="pw-bar-dollars" />
          </g>
        );
      })}
      <polygon points={areaPts} className="pw-chart-area" />
      <polyline points={linePts} className="pw-chart-line" fill="none" />
      {series.map((p, i) => {
        if (p.payPeriodID !== selectedPayPeriodID) return null;
        const x = padL + i * slot + slot / 2;
        const y = padT + plotH - (Math.min(100, p.pctComplete) / 100) * plotH;
        return <circle key={i} cx={x} cy={y} r={3.5} className="pw-chart-dot" />;
      })}

      {/* X-axis week labels (sparse when there are many weeks, to avoid overlap). */}
      {series.map((p, i) => {
        const step = Math.ceil(n / 14);
        if (i % step !== 0 && i !== series.length - 1) return null;
        const cx = padL + i * slot + slot / 2;
        return (
          <text key={`x-${i}`} x={cx} y={baseY + 14} className="pw-chart-xaxis" textAnchor="middle">
            W{p.weekNumber}
          </text>
        );
      })}

      {/* Full-height hit areas for hover + click (easier than hitting a thin bar). */}
      {series.map((p, i) => (
        <rect
          key={`hit-${i}`}
          x={padL + i * slot}
          y={padT}
          width={slot}
          height={plotH}
          fill="transparent"
          className="pw-chart-hit"
          onMouseEnter={() => setHover(i)}
          onClick={() => onPick(p.payPeriodID)}
        />
      ))}

      {tip && (
        <g className="pw-chart-tip" pointerEvents="none">
          <rect x={tip.bx} y={tip.by} width={tip.boxW} height={tip.boxH} rx={7} className="pw-tip-bg" />
          <text x={tip.bx + 10} y={tip.by + 14} className="pw-tip-title">
            W{tip.p.weekNumber} · {fmtShort(tip.p.startDate)}
          </text>
          <text x={tip.bx + 10} y={tip.by + 26} className="pw-tip-line">
            Hourly {hrs(tip.p.totalHourlyMinutes)}h · Piece {hrs(tip.p.totalPieceClockMinutes)}h
          </text>
          <text x={tip.bx + 10} y={tip.by + 37} className="pw-tip-line">
            {money(tip.p.totalPieceDollars)} · {tip.p.pctComplete}% complete
          </text>
        </g>
      )}
    </svg>
  );
};

// ─── Time punches ────────────────────────────────────────────────────────────

const PUNCH_CHIP_LABEL: Record<ProjectWorkPunchType, string> = {
  hourly: 'Hourly time',
  piece: 'Piece time',
  meal: 'Meal',
};

/** An In or Out cell: a plain time, or the struck original above the corrected value. */
const PunchTime: React.FC<{ minutes: number | null; original?: number }> = ({ minutes, original }) => {
  if (original == null) return <>{fmtPunchTime(minutes)}</>;
  return (
    <>
      <span className="pw-punch-was">{fmtPunchTime(original)}</span>
      <span className="pw-punch-now">{fmtPunchTime(minutes)}</span>
    </>
  );
};

/**
 * The employee's actual clock in/out segments for the week — the same list the Project Work
 * email sends, so an admin reviewing on screen sees exactly what the supervisor received.
 * Meals appear as their own unpaid rows; edited punches show the original struck through and
 * hand-entered ones are flagged. Drive time never reaches here (dropped server-side).
 */
const PunchTable: React.FC<{
  punches: ProjectWorkPunch[];
  /** Set while a scope chip is active, e.g. "hourly punches only" — the totals below are
   *  computed from the shown punches, so the reduced number must say what it covers. */
  scopeNote?: string;
}> = ({ punches, scopeNote }) => {
  // Rows are grouped into contiguous days; the API already returns them chronologically.
  const days = useMemo(() => {
    const groups: { date: string; items: ProjectWorkPunch[] }[] = [];
    for (const p of punches) {
      const last = groups[groups.length - 1];
      if (last && last.date === p.entryDate) last.items.push(p);
      else groups.push({ date: p.entryDate, items: [p] });
    }
    return groups;
  }, [punches]);

  if (punches.length === 0) return null;

  const mealMinutes = (p: ProjectWorkPunch): number =>
    p.type === 'meal' && p.outMinutes != null ? p.outMinutes - p.inMinutes : 0;

  const totalWorked = punches.reduce((a, p) => a + p.paidMinutes, 0);
  const totalMeal = punches.reduce((a, p) => a + mealMinutes(p), 0);
  const nMeals = punches.filter((p) => p.type === 'meal').length;

  return (
    <>
      <div className="pw-pieces-subhead pw-hourly-subhead">
        <span>
          <Clock size={12} /> Time punches
        </span>
        <span className="pw-pieces-subhead-total pw-hourly-subhead-total">
          {hrs(totalWorked)} h worked · {nMeals} {nMeals === 1 ? 'meal' : 'meals'} (
          {(totalMeal / 60).toFixed(2)} h unpaid)
          {scopeNote && <span className="pw-scope-note"> · {scopeNote}</span>}
        </span>
      </div>
      <div className="pw-linetable-wrap pw-punchtable-wrap">
        <table className="pw-linetable pw-punchtable">
          <thead>
            <tr>
              <th>Date</th>
              <th>In</th>
              <th>Out</th>
              <th>Detail</th>
              <th className="pw-r">Hours</th>
            </tr>
          </thead>
          <tbody>
            {days.map((g) => {
              const dayWorked = g.items.reduce((a, p) => a + p.paidMinutes, 0);
              const dayMeal = g.items.reduce((a, p) => a + mealMinutes(p), 0);
              const { dow, md } = fmtPunchDate(g.date);
              return g.items.map((p, i) => {
                // Edited wins the flag when a punch was hand-entered and later corrected —
                // the design shows one badge per row, never both.
                const state = p.edited ? 'edited' : p.added ? 'added' : '';
                const rowClass = ['pw-punch-row', p.type === 'meal' ? 'pw-punch-meal' : '', state ? `pw-punch-${state}` : '']
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={`${g.date}-${i}`} className={rowClass}>
                    {i === 0 && (
                      <td className="pw-punch-date" rowSpan={g.items.length}>
                        <span className="pw-punch-dow">{dow}</span>
                        <span className="pw-punch-md">{md}</span>
                        <span className="pw-punch-daytot">
                          {hrs(dayWorked)} h
                          {dayMeal > 0 ? ` · ${(dayMeal / 60).toFixed(2)} h meal` : ''}
                        </span>
                      </td>
                    )}
                    <td className="pw-punch-time">
                      <PunchTime minutes={p.inMinutes} original={p.originalInMinutes} />
                    </td>
                    <td className="pw-punch-time">
                      <PunchTime minutes={p.outMinutes} original={p.originalOutMinutes} />
                    </td>
                    <td className="pw-punch-detail">
                      <div className="pw-punch-detail-inner">
                        <span className={`pw-punch-chip pw-punch-chip-${p.type}`}>{PUNCH_CHIP_LABEL[p.type]}</span>
                        {state === 'edited' && <span className="pw-punch-flag pw-punch-flag-edited">Edited</span>}
                        {state === 'added' && <span className="pw-punch-flag pw-punch-flag-added">Manual add</span>}
                      </div>
                    </td>
                    <td className="pw-r pw-punch-hrs">
                      {p.type === 'meal' ? (
                        <span className="pw-muted">{(mealMinutes(p) / 60).toFixed(2)}</span>
                      ) : (
                        hrs(p.paidMinutes)
                      )}
                    </td>
                  </tr>
                );
              });
            })}
            <tr className="pw-punch-row pw-punch-tot">
              <td className="pw-punch-date" />
              <td colSpan={3}>Week total · worked time</td>
              <td className="pw-r pw-punch-hrs">{hrs(totalWorked)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─── Employee row (detail accordion) ─────────────────────────────────────────

/**
 * Does this row's accordion body have anything in it? The detail region is the punch table
 * plus the two line tables, each of which renders nothing when empty — so a row failing this
 * check would expand to a blank strip. Reads the filtered/scoped row, not the raw employee,
 * so it also answers "is there anything left to expand under the current filter?". Drives
 * Expand all's target set.
 */
const rowHasDetail = (row: FilteredEmployee): boolean =>
  row.punches.length > 0 ||
  row.hourlyLines.length > 0 ||
  row.pieceTasks.length > 0 ||
  row.miscTasks.length > 0;

const EmployeeRow: React.FC<{
  row: FilteredEmployee;
  /** Settled filter term, for highlighting. */
  term: string;
  /** Punch-table scope note while a chip is active. */
  scopeNote?: string;
  open: boolean;
  onToggle: () => void;
}> = ({ row, term, scopeNote, open, onToggle }) => {
  const { emp, hourlyLines, pieceTasks, miscTasks, punches, showHourly, showPiece } = row;
  const hasDetail = rowHasDetail(row);
  // Sub-line counts describe the employee, not the current filter — the tables below are
  // what narrows. Punch edits come off the scoped punches, which is what the table shows.
  const nPieceLines = pieceTasks.length + miscTasks.length;
  const nHourlyLines = hourlyLines.length;
  // A day counts as worked on either clock. Hourly alone would report "0 days" for a
  // piece employee — which the punch table right below now visibly contradicts.
  const nDays = emp.days.filter((d) => d.hourlyMinutes > 0 || d.pieceClockMinutes > 0).length;

  const nPunchEdits = punches.filter((p) => p.edited || p.added).length;

  // Sub-line: "{days} days · {n} punch edits · {n} hourly lines · {m} piece lines", omitting empty clauses.
  const subBits = [`${nDays} ${nDays === 1 ? 'day' : 'days'}`];
  if (nPunchEdits > 0) subBits.push(`${nPunchEdits} punch ${nPunchEdits === 1 ? 'edit' : 'edits'}`);
  if (nHourlyLines > 0) subBits.push(`${nHourlyLines} hourly ${nHourlyLines === 1 ? 'line' : 'lines'}`);
  if (nPieceLines > 0) subBits.push(`${nPieceLines} piece ${nPieceLines === 1 ? 'line' : 'lines'}`);

  // Group hourly cost-code lines by day (lines arrive date-ordered). Day-group headers come
  // from whatever lines survived the filter, so a kept line still reads as "Wed Jun 24"
  // rather than floating context-free, and a day with nothing left drops out.
  const hoursByDay = useMemo(() => {
    const map = new Map<string, { lines: typeof hourlyLines; total: number }>();
    for (const l of hourlyLines) {
      const g = map.get(l.entryDate) ?? { lines: [], total: 0 };
      g.lines.push(l);
      g.total += l.hourlyMinutes;
      map.set(l.entryDate, g);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [hourlyLines]);

  // Group piece + misc lines by day
  const byDay = useMemo(() => {
    const map = new Map<string, { piece: typeof pieceTasks; misc: typeof miscTasks; total: number }>();
    for (const p of pieceTasks) {
      const g = map.get(p.entryDate) ?? { piece: [], misc: [], total: 0 };
      g.piece.push(p);
      g.total += p.amountClaimed;
      map.set(p.entryDate, g);
    }
    for (const m of miscTasks) {
      const g = map.get(m.entryDate) ?? { piece: [], misc: [], total: 0 };
      g.misc.push(m);
      g.total += m.amountClaimed;
      map.set(m.entryDate, g);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [pieceTasks, miscTasks]);

  return (
    <div
      className={`pw-emp ${open ? 'open' : ''}${hasDetail ? '' : ' pw-emp-inert'}${
        row.direct ? ' fmatch' : row.deepOnly ? ' fctx' : ''
      }`}
    >
      <button
        className="pw-emp-main"
        onClick={hasDetail ? onToggle : undefined}
        aria-expanded={hasDetail ? open : undefined}
      >
        <div className="pw-emp-id">
          <span className="pw-avatar">{initials(emp.name)}</span>
          <span className="pw-emp-name">
            <Highlight text={emp.name} term={term} />
            <span className="pw-emp-alt">
              <Highlight text={emp.altEmployeeID} term={term} />
              {emp.supervisorName && (
                <span className="pw-emp-supr">
                  {' · super '}
                  <Highlight text={emp.supervisorName} term={term} />
                </span>
              )}
            </span>
            <span className="pw-emp-sub">{subBits.join(' · ')}</span>
          </span>
        </div>
        <div className="pw-emp-val pw-hourly">{hrs(emp.hourlyMinutes)} h</div>
        <div className="pw-emp-val pw-muted">
          {emp.pieceClockMinutes > 0 ? `${hrs(emp.pieceClockMinutes)} h` : '—'}
        </div>
        <div className="pw-emp-val pw-piece">{emp.pieceDollars > 0 ? money(emp.pieceDollars) : '—'}</div>
        {hasDetail ? <ChevronRight size={16} className="pw-emp-caret" /> : <span />}
      </button>

      {open && hasDetail && (
        <div className="pw-emp-detail">
          <PunchTable punches={punches} scopeNote={scopeNote} />

          {showHourly && nHourlyLines > 0 && (
            <>
            <div className="pw-pieces-subhead pw-hourly-subhead">
              <span><Clock size={12} /> Hours by Cost Code</span>
              <span className="pw-pieces-subhead-total pw-hourly-subhead-total">{hrs(emp.hourlyMinutes)} h · by day</span>
            </div>
            <div className="pw-linetable-wrap">
              <table className="pw-linetable pw-htable">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th className="pw-r">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {hoursByDay.map(([date, g]) => {
                    const { dow, md } = fmtDayLabel(date);
                    return (
                      <React.Fragment key={date}>
                        <tr className="pw-jobgroup-row pw-hourly-grp">
                          <td colSpan={3}>
                            <div className="pw-jobgroup">
                              <span className="pw-dow-badge">{dow.toUpperCase()}</span>
                              <span className="pw-jobgroup-date">{md}</span>
                              <span className="pw-jobgroup-total">{hrs(g.total)} h</span>
                            </div>
                          </td>
                        </tr>
                        {g.lines.map((l, i) => (
                          <tr key={`${date}-${l.code}-${i}`}>
                            <td className="pw-mono pw-code">
                              <Highlight text={l.code} term={term} />
                            </td>
                            <td className="pw-lt-desc">
                              <Highlight text={l.description} term={term} />
                            </td>
                            <td className="pw-r pw-hourly pw-ht-hrs">{hrs(l.hourlyMinutes)} h</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {showPiece && nPieceLines > 0 && (
            <>
            <div className="pw-pieces-subhead">
              <span>Pieces Claimed</span>
              <span className="pw-pieces-subhead-total">{money(emp.pieceDollars)} · by day</span>
            </div>
            <div className="pw-linetable-wrap">
              <table className="pw-linetable">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Phase / Lot</th>
                    <th>Type</th>
                    <th className="pw-r">Claimed $</th>
                    <th className="pw-r">% Claimed</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay.map(([date, g]) => {
                    const { dow, md } = fmtDayLabel(date);
                    return (
                      <React.Fragment key={date}>
                        <tr className="pw-jobgroup-row">
                          <td colSpan={6}>
                            <div className="pw-jobgroup">
                              <span className="pw-dow-badge">{dow.toUpperCase()}</span>
                              <span className="pw-jobgroup-date">{md}</span>
                              <span className="pw-jobgroup-total">{money(g.total)}</span>
                            </div>
                          </td>
                        </tr>
                        {g.piece.map((p) => (
                          <tr key={`p${p.taskID}-${p.entryDate}-${p.amountClaimed}`}>
                            <td className="pw-mono pw-code">
                              {p.code ? <Highlight text={p.code} term={term} /> : '—'}
                            </td>
                            <td className="pw-lt-desc">
                              <Highlight text={p.taskName} term={term} />
                            </td>
                            <td className="pw-muted">
                              {p.intermediateName ? <Highlight text={p.intermediateName} term={term} /> : '—'}
                            </td>
                            <td>
                              <span className="pw-type pw-type-piece">Piece</span>
                            </td>
                            <td className="pw-r pw-piece">{money(p.amountClaimed)}</td>
                            <td
                              className={`pw-r ${p.pctClaimed != null && p.pctClaimed >= 100 ? 'pw-ok' : 'pw-warn'}`}
                            >
                              {p.pctClaimed != null ? `${p.pctClaimed}%` : '—'}
                            </td>
                          </tr>
                        ))}
                        {g.misc.map((m) => (
                          <tr key={`m${m.miscEmployeeTaskID}`}>
                            <td className="pw-mono pw-code">—</td>
                            <td className="pw-lt-desc">
                              <Highlight text={m.miscDescription} term={term} />
                            </td>
                            <td className="pw-muted">
                              {m.intermediateName ? <Highlight text={m.intermediateName} term={term} /> : '—'}
                            </td>
                            <td>
                              <span className="pw-type pw-type-misc">Misc</span>
                            </td>
                            <td className="pw-r pw-piece">{money(m.amountClaimed)}</td>
                            <td className="pw-r pw-muted">—</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const ProjectWorkPage: React.FC = () => {
  // The week comes from the header pill (SiteContext) — this page has no picker of
  // its own, so navigating away and back keeps whatever week the rest of the app is on.
  const { payPeriods, payPeriodID } = useSiteContext();
  const selectPayPeriod = useSelectPayPeriod();

  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedProjectID, setSelectedProjectID] = useState<number | null>(null);
  const [supFilter, setSupFilter] = useState<number | 'all'>('all');
  const [supervisors, setSupervisors] = useState<ProjectWorkSupervisorOption[]>([]);

  const [list, setList] = useState<ProjectWorkList | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [detail, setDetail] = useState<ProjectWorkDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Employee accordions open on the project detail view, by employeeID.
  const [expandedEmps, setExpandedEmps] = useState<Set<number>>(new Set());

  // List-view search. Resets whenever the underlying set is refetched, so a term typed
  // for one week can't silently hide every project in the next one.
  const [projectFilter, setProjectFilter] = useState('');
  const projTerm = normalizeTerm(useDebouncedValue(projectFilter));

  // Detail-view filters. Deliberately survive week navigation — checking one cost code
  // across several weeks shouldn't mean retyping it — and reset on project change.
  const [rollupFilter, setRollupFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [employeeKind, setEmployeeKind] = useState<EmployeeScope>('all');
  const rollupTerm = normalizeTerm(useDebouncedValue(rollupFilter));
  const empTerm = normalizeTerm(useDebouncedValue(employeeFilter));

  const [chartMetric, setChartMetric] = useState<'hours' | 'dollars'>('hours');
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const currentPP: PayPeriod | undefined = payPeriods.find((p) => p.PayPeriodID === payPeriodID);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  // Load supervisors once for the admin filter.
  useEffect(() => {
    getProjectWorkSupervisors().then(setSupervisors).catch(() => setSupervisors([]));
  }, []);

  // Fetch list on (week, filter) change.
  useEffect(() => {
    if (!payPeriodID) return;
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    setProjectFilter('');
    getProjectWorkList(payPeriodID, supFilter === 'all' ? undefined : supFilter)
      .then((data) => {
        if (!cancelled) setList(data);
      })
      .catch(() => {
        if (!cancelled) setListError('Could not load project activity.');
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payPeriodID, supFilter]);

  // Fetch detail when viewing a project.
  useEffect(() => {
    if (view !== 'detail' || selectedProjectID == null || !payPeriodID) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setExpandedEmps(new Set()); // a different project/week starts fully collapsed
    getProjectWorkDetail(selectedProjectID, payPeriodID)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setDetailError('Could not load project detail.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, selectedProjectID, payPeriodID]);

  const clearFilters = () => {
    setRollupFilter('');
    setEmployeeFilter('');
    setEmployeeKind('all');
  };

  const openProject = (projectID: number) => {
    setSelectedProjectID(projectID);
    setView('detail');
    setDetail(null);
    clearFilters();
    window.scrollTo({ top: 0 });
  };

  const backToList = () => {
    setView('list');
    setSelectedProjectID(null);
    clearFilters();
    window.scrollTo({ top: 0 });
  };

  // ── Filtered views: the project list, then the two detail regions ───────
  const projView = useMemo(
    () => filterProjectList(list?.projects ?? [], projTerm),
    [list, projTerm]
  );
  const rollupView = useMemo(
    () => filterCodeRollup(detail?.hourlyByCode ?? [], rollupTerm),
    [detail, rollupTerm]
  );
  const empView = useMemo(
    () => filterEmployees(detail?.employees ?? [], empTerm, employeeKind),
    [detail, empTerm, employeeKind]
  );
  const empScopeNote = punchScopeNote(employeeKind);

  /* A row whose hit is buried in its tables opens itself, so the matching line is visible
   * rather than hidden behind a collapsed row. Same "new baseline" rule as the Projects
   * page: clearing the term leaves those rows open. */
  useEffect(() => {
    if (empView.revealIDs.length === 0) return;
    setExpandedEmps((prev) => new Set([...prev, ...empView.revealIDs]));
  }, [empView]);

  // Rows with a body to open under the current filter — Expand all works on these, and they
  // decide when each button is a no-op (stale IDs from another project/week are ignored).
  const expandableEmpIDs = useMemo(
    () => empView.rows.filter(rowHasDetail).map((r) => r.emp.employeeID),
    [empView]
  );
  const allEmpsExpanded = expandableEmpIDs.length > 0 && expandableEmpIDs.every((id) => expandedEmps.has(id));
  const noEmpsExpanded = !expandableEmpIDs.some((id) => expandedEmps.has(id));
  const expandAllEmps = () => setExpandedEmps(new Set(expandableEmpIDs));
  const collapseAllEmps = () => setExpandedEmps(new Set());
  const toggleEmp = (employeeID: number) =>
    setExpandedEmps((prev) => {
      const next = new Set(prev);
      next.has(employeeID) ? next.delete(employeeID) : next.add(employeeID);
      return next;
    });

  const handleExport = async (format: 'excel' | 'pdf') => {
    setExporting(true);
    try {
      await downloadProjectWorkExport(format, payPeriodID, {
        projectID: view === 'detail' && selectedProjectID != null ? selectedProjectID : undefined,
        supervisorEmployeeID: view === 'list' && supFilter !== 'all' ? supFilter : undefined,
      });
      showToast(`${format === 'excel' ? 'Excel' : 'PDF'} export ready`);
    } catch {
      showToast('Export failed — please retry');
    } finally {
      setExporting(false);
    }
  };

  // Clicking a bar on the trend chart moves the whole app to that week, not just this page.
  const jumpToWeek = (ppID: number) => {
    const pp = payPeriods.find((p) => p.PayPeriodID === ppID);
    if (pp) selectPayPeriod(pp);
  };

  const weekBadge = currentPP ? `W${currentPP.WeekNumber}` : '—';
  const weekLong = currentPP ? `Week ${currentPP.WeekNumber}` : '—';

  // Supervisor filter options, in the shared selector's shape. No counts: the list is
  // already scoped to the chosen supervisor, so a per-option project count isn't known.
  const supMenuOptions: FloatMenuOption[] = useMemo(
    () => [
      { value: '', label: 'All supervisors' },
      ...supervisors.map((s) => ({ value: String(s.employeeID), label: s.name })),
    ],
    [supervisors]
  );

  const supValueLabel =
    supFilter === 'all'
      ? 'All supervisors'
      : supervisors.find((s) => s.employeeID === supFilter)?.name ?? 'All supervisors';

  // ─── Render: exports (shared header-right) ───
  const HeaderRight = (
    <div className="pw-head-right">
      <div className="pw-exports">
        <button className="pw-btn-sm" disabled={exporting} onClick={() => handleExport('excel')}>
          <FileSpreadsheet size={14} /> Excel
        </button>
        <button className="pw-btn-sm" disabled={exporting} onClick={() => handleExport('pdf')}>
          <Printer size={14} /> PDF
        </button>
      </div>
    </div>
  );

  return (
    <div className="pw">
      {view === 'list' ? (
        <>
          <div className="pw-head">
            <div className="pw-head-icon">
              <Briefcase size={24} />
            </div>
            <div className="pw-head-main">
              <h1 className="pw-title">Project Work</h1>
              <div className="pw-subline">
                <span className="pw-scope-pill">
                  <ShieldCheck size={13} /> ADMIN · ALL ACTIVITY
                </span>
                <span className="pw-sub-desc">
                  Projects with hours &amp; piece activity for {weekLong}.
                </span>
              </div>
            </div>
            {HeaderRight}
          </div>

          {supervisors.length > 1 && (
            <div className="pw-filterbar">
              <FloatMenuButton
                label="Super"
                valueLabel={supValueLabel}
                isPlaceholder={supFilter === 'all'}
                isActive={supFilter !== 'all'}
                options={supMenuOptions}
                selectedValue={supFilter === 'all' ? '' : String(supFilter)}
                onSelect={(v) => setSupFilter(v === '' ? 'all' : Number(v))}
                searchable
              />
              <span className="pw-filter-note">
                {list ? `${list.projects.length} projects with activity` : ''}
              </span>
            </div>
          )}

          <div className="pw-body">
            {listLoading && list && <LoadingOverlay />}

            {list ? (
              <>
                <div className="pw-kpis">
                  <div className="pw-kpi">
                    <div className="pw-kpi-label">
                      <Building2 size={14} /> PROJECTS
                    </div>
                    <div className="pw-kpi-val">{list.totals.projectCount}</div>
                  </div>
                  <div className="pw-kpi">
                    <div className="pw-kpi-label pw-green">
                      <Clock size={14} /> HOURLY HOURS
                    </div>
                    <div className="pw-kpi-val">{hrs(list.totals.totalHourlyMinutes)}</div>
                  </div>
                  <div className="pw-kpi">
                    <div className="pw-kpi-label pw-blue">
                      <Package size={14} /> PIECE $
                    </div>
                    <div className="pw-kpi-val">{moneyShort(list.totals.totalPieceDollars)}</div>
                  </div>
                  <div className="pw-kpi">
                    <div className="pw-kpi-label">
                      <Users size={14} /> EMPLOYEES
                    </div>
                    <div className="pw-kpi-val">{list.totals.employeeCount}</div>
                  </div>
                </div>

                <div className="pw-sect">
                  <span>
                    <Layers size={14} /> PROJECTS WITH ACTIVITY · {weekLong.toUpperCase()}
                  </span>
                  <span className="pw-sect-note">sorted by project #</span>
                </div>

                {list.projects.length === 0 ? (
                  <div className="pw-empty">
                    <Inbox size={26} />
                    <div className="pw-empty-title">No project activity</div>
                    <div className="pw-empty-sub">
                      No projects had hours or piece claims in {weekBadge}.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="pw-list-filter">
                      <FilterBar
                        value={projectFilter}
                        onChange={setProjectFilter}
                        placeholder="Filter projects — project # or name…"
                        label="Filter projects"
                        matches={projView.matches}
                        total={list.projects.length}
                        active={projTerm !== ''}
                      />
                    </div>
                    {projView.projects.length === 0 ? (
                      <div className="pw-list-nomatch">
                        <FilterEmpty
                          noun="projects"
                          term={projTerm}
                          onClear={() => setProjectFilter('')}
                        />
                      </div>
                    ) : (
                      <div className="pw-proj-list">
                        {projView.projects.map((p) => (
                          <ProjectRow
                            key={p.projectID}
                            row={p}
                            term={projTerm}
                            onOpen={() => openProject(p.projectID)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : listError ? (
              <div className="pw-error">{listError}</div>
            ) : (
              <CenteredLoader />
            )}
          </div>
        </>
      ) : (
        <>
          <div className="pw-backbar">
            <button className="pw-back" onClick={backToList}>
              <ChevronLeft size={16} /> All projects
            </button>
          </div>

          {!detail && detailLoading && <CenteredLoader label="Loading project…" />}
          {!detail && detailError && <div className="pw-error">{detailError}</div>}

          {detail && (
            <>
              <div className="pw-head">
                <div className="pw-head-icon">
                  <Building2 size={24} />
                </div>
                <div className="pw-head-main">
                  <h1 className="pw-title">
                    {detail.name} <span className="pw-title-alt">{detail.altProjectID}</span>
                  </h1>
                  <div className="pw-subline">
                    {detail.supervisors.length > 0 && (
                      <SupervisorTags supervisors={detail.supervisors} />
                    )}
                    {fullAddress(detail) && (
                      <span className="pw-sub-meta">
                        <MapPin size={13} /> {fullAddress(detail)}
                      </span>
                    )}
                  </div>
                </div>
                {HeaderRight}
              </div>

              <div className="pw-body">
                {detailLoading && <LoadingOverlay />}

              {/* Trend cards */}
              <div className="pw-trends">
                <div className="pw-tc">
                  <div className="pw-tc-top">
                    <span className="pw-tc-label">HOURLY HOURS</span>
                    <div className="pw-tc-delta">
                      <DeltaBadge trend={toHourTrend(detail.trends.hourlyMinutes)} suffix="h" />
                      <span className="pw-tc-vs">vs last week</span>
                    </div>
                  </div>
                  <div className="pw-tc-val pw-green">{hrs(detail.totalHourlyMinutes)} h</div>
                  <div className="pw-tc-foot">
                    Piece {hrs(detail.totalPieceClockMinutes)} h · {detail.employees.length} working
                  </div>
                </div>
                <div className="pw-tc">
                  <div className="pw-tc-top">
                    <span className="pw-tc-label">PIECE DOLLARS</span>
                    <div className="pw-tc-delta">
                      <DeltaBadge trend={detail.trends.pieceDollars} suffix="$" />
                      <span className="pw-tc-vs">vs last week</span>
                    </div>
                  </div>
                  <div className="pw-tc-val pw-blue">{money(detail.totalPieceDollars)}</div>
                  <div className="pw-tc-foot">{money(detail.cumPieceDollars)} claimed to date</div>
                </div>
                <div className="pw-tc">
                  <div className="pw-tc-top">
                    <span className="pw-tc-label">PIECES % COMPLETE</span>
                    <div className="pw-tc-delta">
                      <DeltaBadge trend={detail.trends.pctComplete} suffix="pt" points />
                      <span className="pw-tc-vs">vs last week</span>
                    </div>
                  </div>
                  <div className="pw-tc-val pw-purple">{detail.pctComplete}%</div>
                  <div className="pw-progress">
                    <div className="pw-progress-fill" style={{ width: `${Math.min(100, detail.pctComplete)}%` }} />
                  </div>
                  <div className="pw-tc-foot">
                    {money(detail.cumPieceDollars)} of {money(detail.budgetedPieceDollars)}
                  </div>
                </div>
              </div>

              {/* Activity chart */}
              <div className="pw-chartcard">
                <div className="pw-chart-head">
                  <span className="pw-chart-title">
                    <BarChart3 size={15} /> Activity over time
                  </span>
                  <div className="pw-chart-legend">
                    <span className="pw-lg pw-lg-hourly">Hourly</span>
                    <span className="pw-lg pw-lg-piececlock">Piece</span>
                    <span className="pw-lg pw-lg-line">% complete</span>
                  </div>
                  <div className="pw-seg">
                    <button
                      className={chartMetric === 'hours' ? 'active' : ''}
                      onClick={() => setChartMetric('hours')}
                    >
                      Hours
                    </button>
                    <button
                      className={chartMetric === 'dollars' ? 'active' : ''}
                      onClick={() => setChartMetric('dollars')}
                    >
                      Piece $
                    </button>
                  </div>
                </div>
                <ActivityChart
                  series={detail.series}
                  metric={chartMetric}
                  selectedPayPeriodID={detail.payPeriodID}
                  onPick={jumpToWeek}
                />
              </div>

              {/* Labor by cost code — hourly rollup across the crew */}
              {detail.hourlyByCode.length > 0 && (
                <>
                  <div className="pw-sect">
                    <span>
                      <Layers size={14} /> LABOR BY COST CODE · HOURLY
                    </span>
                    <span className="pw-sect-note">
                      {hrs(detail.totalHourlyMinutes)} h across {detail.hourlyByCode.length} code{detail.hourlyByCode.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="pw-rollup-card">
                    <FilterBar
                      value={rollupFilter}
                      onChange={setRollupFilter}
                      placeholder="Filter cost codes — code or description…"
                      label="Filter cost codes"
                      matches={rollupView.matches}
                      total={detail.hourlyByCode.length}
                      active={rollupTerm !== ''}
                    />
                    {rollupView.codes.length === 0 ? (
                      <FilterEmpty noun="cost codes" term={rollupTerm} onClear={() => setRollupFilter('')} />
                    ) : (
                    <table className="pw-linetable pw-rolltable">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Description</th>
                          <th>Crew</th>
                          <th className="pw-barcol">Share of hours</th>
                          <th className="pw-r">Hours</th>
                          <th className="pw-r">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rollupView.codes.map((cc) => {
                          const pct = detail.totalHourlyMinutes > 0
                            ? (cc.totalHourlyMinutes / detail.totalHourlyMinutes) * 100
                            : 0;
                          return (
                            <tr key={cc.code}>
                              <td className="pw-mono pw-code">
                                <Highlight text={cc.code} term={rollupTerm} />
                              </td>
                              <td className="pw-lt-desc">
                                <Highlight text={cc.description} term={rollupTerm} />
                              </td>
                              <td className="pw-muted">{cc.employeeCount} emp</td>
                              <td className="pw-barcell">
                                <div className="pw-codebar"><i style={{ width: `${pct.toFixed(1)}%` }} /></div>
                              </td>
                              <td className="pw-r pw-roll-hrs">{hrs(cc.totalHourlyMinutes)} h</td>
                              <td className="pw-r pw-roll-pct">{Math.round(pct)}%</td>
                            </tr>
                          );
                        })}
                        {/* Never filtered — this is the week's real total. */}
                        <tr className="pw-rolltot">
                          <td colSpan={3}>Total hourly labor</td>
                          <td />
                          <td className="pw-r pw-roll-hrs">{hrs(detail.totalHourlyMinutes)} h</td>
                          <td className="pw-r">100%</td>
                        </tr>
                      </tbody>
                    </table>
                    )}
                  </div>
                </>
              )}

              {/* Employees */}
              <div className="pw-sect">
                <span>
                  <Users size={14} /> EMPLOYEES · {detail.employees.length} with time
                </span>
                <div className="pw-sect-actions">
                  <span className="pw-sect-note">tap a row for daily hours &amp; piece detail</span>
                  {expandableEmpIDs.length > 0 && (
                    <>
                      <button
                        className="pw-btn-sm"
                        type="button"
                        onClick={expandAllEmps}
                        disabled={allEmpsExpanded}
                      >
                        <ChevronDown size={14} /> Expand all
                      </button>
                      <button
                        className="pw-btn-sm"
                        type="button"
                        onClick={collapseAllEmps}
                        disabled={noEmpsExpanded}
                      >
                        <ChevronRight size={14} /> Collapse all
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="pw-emp-card">
                {detail.employees.length > 0 && (
                  <FilterBar
                    value={employeeFilter}
                    onChange={setEmployeeFilter}
                    placeholder="Filter employees — name, ID, supervisor, cost code, description, lot…"
                    label="Filter employees"
                    matches={empView.rows.length}
                    total={detail.employees.length}
                    active={empTerm !== '' || employeeKind !== 'all'}
                  >
                    {/* A scope, not a search: cuts rows, sub-sections and punch rows at once. */}
                    <div className="fb-chips" role="radiogroup" aria-label="Work type">
                      {([
                        ['all', 'All'],
                        ['hourly', 'Hourly only'],
                        ['piece', 'Piece only'],
                      ] as [EmployeeScope, string][]).map(([kind, label]) => (
                        <button
                          key={kind}
                          type="button"
                          role="radio"
                          aria-checked={employeeKind === kind}
                          className={`fb-chip${employeeKind === kind ? ' on' : ''}`}
                          onClick={() => setEmployeeKind(kind)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </FilterBar>
                )}
                <div className="pw-emp-head">
                  <span>Employee</span>
                  <span className="pw-r">Hourly</span>
                  <span className="pw-r">Piece</span>
                  <span className="pw-r">Piece $</span>
                  <span />
                </div>
                {/* A week with genuinely no employees keeps its own empty state (the zero
                    totals row below); this one only speaks for the filter. */}
                {detail.employees.length > 0 && empView.rows.length === 0 ? (
                  <FilterEmpty
                    noun="employees"
                    term={empTerm}
                    onClear={() => {
                      setEmployeeFilter('');
                      setEmployeeKind('all');
                    }}
                  />
                ) : (
                  empView.rows.map((r) => (
                    <EmployeeRow
                      key={r.emp.employeeID}
                      row={r}
                      term={empTerm}
                      scopeNote={empScopeNote}
                      open={expandedEmps.has(r.emp.employeeID)}
                      onToggle={() => toggleEmp(r.emp.employeeID)}
                    />
                  ))
                )}
                <div className="pw-emp-foot">
                  <span>Totals</span>
                  <span className="pw-r pw-hourly">{hrs(detail.totalHourlyMinutes)} h</span>
                  <span className="pw-r pw-muted">{hrs(detail.totalPieceClockMinutes)} h</span>
                  <span className="pw-r pw-piece">{money(detail.totalPieceDollars)}</span>
                  <span />
                </div>
              </div>
              </div>
            </>
          )}
        </>
      )}

      {toast && <div className="pw-toast">{toast}</div>}
    </div>
  );
};

// ─── List project row ────────────────────────────────────────────────────────

const ProjectRow: React.FC<{ row: ProjectWorkListRow; term: string; onOpen: () => void }> = ({
  row,
  term,
  onOpen,
}) => (
  <button className="pw-proj-row" onClick={onOpen}>
    <div className="pw-pr-name">
      <span className="pw-pr-id">
        <Highlight text={row.altProjectID} term={term} />
      </span>
      <span className="pw-pr-title">
        <Highlight text={row.name} term={term} />
      </span>
      <span className="pw-pr-meta">
        {row.supervisors.length > 0 && <SupervisorTags supervisors={row.supervisors} />}
        <span className="pw-pr-meta-stat">
          <Users size={12} /> {row.employeeCount} working · {row.pctComplete}% complete
        </span>
      </span>
    </div>
    <div className="pw-pr-spark">
      <span className="pw-pr-collabel">
        <Clock size={11} /> 8-WK HOURS
      </span>
      <Sparkline points={row.sparkline} />
    </div>
    <div className="pw-pr-metric">
      <span className="pw-pr-mval pw-hourly">{hrs(row.totalHourlyMinutes)} h</span>
      <span className="pw-pr-mlabel">HOURLY</span>
    </div>
    <div className="pw-pr-metric pw-pr-piececlock">
      <span className="pw-pr-mval pw-muted">
        {row.totalPieceClockMinutes > 0 ? `${hrs(row.totalPieceClockMinutes)} h` : '—'}
      </span>
      <span className="pw-pr-mlabel">PIECE</span>
    </div>
    <div className="pw-pr-metric">
      <span className="pw-pr-mval pw-piece">{moneyShort(row.totalPieceDollars)}</span>
      <span className="pw-pr-mlabel">PIECES</span>
    </div>
    <div className="pw-pr-go">
      <ChevronRight size={18} />
    </div>
  </button>
);

export default ProjectWorkPage;
